import { useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { BsFillEyeFill, BsFillEyeSlashFill } from 'react-icons/bs';
import { MdEmail } from 'react-icons/md';
import { RiLockPasswordFill } from 'react-icons/ri';
import { HashLink as Link } from 'react-router-hash-link';
import { toast } from 'react-toastify';
import { getLocalStorage, postWithoutToken, setLocalStorage } from '../../API/Api';
import { validateEmail } from '../../assets/Helper/utils';
import { useNavigate } from 'react-router-dom';
import { PATHS, SESSION_PATHS } from '../../routes/paths';
import LOGO from '../../assets/images/comniqAI/comniq-logo-dark.svg';
import MOCKUP from '../../assets/images/comniqAI/mockup.svg';
import AuthLayout from './AuthLayout';
import ButtonLoader from '../../components/ButtonLoader';
import { useGoogleLogin } from '@react-oauth/google';
import axios from 'axios';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest } from '../../config/authConfig';
import { jwtDecode } from 'jwt-decode';
import showToast from '../../components/Toast/toastHelper';
import { getIpAddress } from '../../utils/ipAddress';

const ValidationErrors = {
  empty: {
    email: 'Email id Required',
    password: 'Password Required',
  },
  invalid: {
    email: 'Invalid Email',
  },
};

const LoginForm = () => {
  const recaptchaRef = useRef(null);
  const { instance, inProgress } = useMsal();
  const [captchaValue, setCaptchaValue] = useState(null);
  const [showPwd, setShowPwd] = useState(false);
  const [loader, setLoader] = useState(false);
  const [values, setValues] = useState({
    email: '',
    password: '',
    type: '',
  });

  const [errors, setErrors] = useState({
    email: '',
    password: '',
  });

  const Navigate = useNavigate();
  const { email, password } = values;

  const handleChange = (e) => {
    setValues({ ...values, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: '' });
  };

  useEffect(() => {
    let token = getLocalStorage('apiToken');
    if (token) {
      Navigate(PATHS.DASHBOARD);
    }
  }, [Navigate]);

  const handleClick = () => {
    setLoader(true);
    const tempErrors = { ...errors };
    Object.keys(values).forEach((key) => {
      if (!values[key]) {
        tempErrors[key] = ValidationErrors.empty[key];
      } else {
        if (key === 'email' && !validateEmail(values[key])) {
          tempErrors.email = ValidationErrors.invalid.email;
        }
      }
    });
    setErrors(tempErrors);

    if (Object.values(tempErrors).filter((obj) => !!obj)?.length > 0) {
      setLoader(false);
      return false;
    }
    if (!captchaValue) {
      setLoader(false);
      toast.error('Please complete the CAPTCHA');
      return;
    }

    postWithoutToken('login/', values)
      .then((response) => {
        if (response.status === 200) {
          console.log('responselogin: ', response);
          setLocalStorage('sidebarToggle', 'false');
          setLocalStorage('apiToken', response?.data?.access);
          setLocalStorage('FullName', response?.data?.first_name + ' ' + response?.data?.last_name);
          setLocalStorage('userDetails', response?.data);
          toast.success(response.message);

          const subs_id = response?.data?.plan?.subs_id;
          const subscription = response?.data?.plan?.subscription;
          const isSubActive = response?.data?.plan?.is_subscription_status === true;

          if (subscription !== null) {
            window.location.href = PATHS.DASHBOARD;
            return;
          }

          if (subs_id === null && isSubActive === false) {
            window.location.href = PATHS.PRICING;
            return;
          }

          window.location.href = PATHS.CHATBOT;
        } else if (response.status === 400) {
          showToast(response.message, 'error');
        } else if (response.status === 428) {
          showToast(response.message, 'info');
          setLocalStorage('userEmail', values.email);
          Navigate(SESSION_PATHS.OTP_VERIFICATION(response?.data?.accesstoken));
        }
      })
      .catch((error) => {
        console.error('error: ', error);
      })
      .finally(() => {
        setLoader(false);
      });
  };

  const handleGoogleAuth = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        const userInfo = await axios
          .get('https://www.googleapis.com/oauth2/v3/userinfo', {
            headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
          })
          .then((res) => res.data);
        const payload = {
          access_token: tokenResponse.access_token,
          email: userInfo.email,
          first_name: userInfo?.family_name,
          last_name: userInfo?.given_name,
          name: userInfo.name,
          googleId: userInfo.sub,
          ip_address: await getIpAddress(),
        };
        const response = await postWithoutToken('auth/google', payload);
        if (response?.status === 200) {
          setLocalStorage('sidebarToggle', 'false');
          setLocalStorage('apiToken', response?.data?.access);
          setLocalStorage('FullName', response?.data?.first_name + ' ' + response?.data?.last_name);
          setLocalStorage('userDetails', response?.data);

          const subs_id = response?.data?.plan?.subs_id;
          const subscription = response?.data?.plan?.subscription;

          if (subs_id === null) {
            Navigate(PATHS.PRICING, { replace: true });
          } else if (subscription !== null) {
            Navigate(PATHS.DASHBOARD, { replace: true });
          } else {
            Navigate(PATHS.CHATBOT, { replace: true });
          }
          window.location.reload();
          toast.success(response.message || 'Google sign-in successful.', { variant: 'success' });
        } else {
          toast.error(response.message || 'Invalid email or password', { variant: 'error' });
        }
      } catch (error) {
        console.error('Google login process failed:', error);
        toast.error(error.message || 'An error occurred during Google login.', { variant: 'error' });
      }
    },
  });

  const handleMicrosoftAuth = async () => {
    if (inProgress !== InteractionStatus.None) {
      console.warn('Interaction already in progress...');
      return;
    }

    try {
      const userInfo = await instance.loginPopup(loginRequest);

      const decoded = jwtDecode(userInfo?.accessToken);

      const payload = {
        access_token: userInfo.account.idToken,
        email: userInfo.account.username,
        first_name: decoded.given_name,
        last_name: decoded.family_name,
        name: userInfo.account.name,
        microsoftId: userInfo.account?.idTokenClaims?.sub,
        ip_address: await getIpAddress(),
      };

      // Calling the `loginWithMicrosoft` function
      const response = await postWithoutToken('auth/microsoft', payload);

      if (response?.status === 200) {
        setLocalStorage('sidebarToggle', 'false');
        setLocalStorage('apiToken', response?.data?.access);
        setLocalStorage('FullName', response?.data?.first_name + ' ' + response?.data?.last_name);
        setLocalStorage('userDetails', response?.data);

        const subs_id = response?.data?.plan?.subs_id;
        const subscription = response?.data?.plan?.subscription;

        if (subs_id === null) {
          Navigate(PATHS.PRICING, { replace: true });
        } else if (subscription !== null) {
          Navigate(PATHS.DASHBOARD, { replace: true });
        } else {
          Navigate(PATHS.CHATBOT, { replace: true });
        }
        toast.success(response.message || 'Microsoft sign-up successful.');
        window.location.reload();
      } else {
        toast.error(response.message || 'Registration failed. Please try again.');
      }
    } catch (err) {
      console.error('Microsoft registration failed:', err);
    }
  };

  const onFormSubmit = (e) => {
    e.preventDefault();
  };

  return (
    <AuthLayout title='Sign in' subtitle='Don’t have an account?' linkText='Sign up' linkTo={SESSION_PATHS.REGISTER} LeftImage={MOCKUP} Logo={LOGO}>
      <form onSubmit={onFormSubmit}>
        <div class='social-buttons'>
          <button type='button' className='btn-social btn-google flex-fill d-flex align-items-center justify-content-center gap-2' onClick={() => handleGoogleAuth()}>
            {/* <FcGoogle size={20} /> */}
            <svg width='20' height='20' viewBox='0 0 24 24'>
              <path fill='#4285F4' d='M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z' />
              <path fill='#34A853' d='M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z' />
              <path fill='#FBBC05' d='M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z' />
              <path fill='#EA4335' d='M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z' />
            </svg>
            <span className='outfit-medium'>Google</span>
          </button>
          <button type='button' className='btn-social btn-microsoft flex-fill d-flex align-items-center justify-content-center gap-2' onClick={() => handleMicrosoftAuth()}>
            <svg width='20' height='20' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'>
              <rect x='0' y='0' width='11.377' height='11.372' fill='#F35325' />
              <rect x='12.623' y='0' width='11.377' height='11.372' fill='#81BC06' />
              <rect x='0' y='12.623' width='11.377' height='11.377' fill='#05A6F0' />
              <rect x='12.623' y='12.623' width='11.377' height='11.377' fill='#FFBA08' />
            </svg>
            <span className='outfit-medium'>Microsoft</span>
          </button>
        </div>
        {/* Divider */}
        <div class='auth-divider'>
          <div class='divider-line'></div>
          <span class='divider-text'>Or sign in with</span>
          <div class='divider-line'></div>
        </div>

        <div className='form-group position-relative input-GPT'>
          <label htmlFor='email' className='auth-label outfit-semibold'>
            Email*
          </label>

          <div className='input-with-icon'>
            <MdEmail className='input-icon' />

            <input type='text' className='form-control auth-input' id='email' name='email' value={email} placeholder='Email' autoComplete='off' onChange={handleChange} />
          </div>

          {errors.email && <p className='text-danger insta-smart-error'>{errors.email}</p>}
        </div>

        <div className='form-group position-relative input-GPT'>
          <div className='d-flex justify-content-between align-items-center mb-1'>
            <label className='auth-label outfit-semibold'>Password*</label>

            <Link to={SESSION_PATHS.FORGOT_PASSWORD} className='auth-link outfit-bold'>
              Forgot password?
            </Link>
          </div>
          <div className='input-with-icon'>
            <RiLockPasswordFill className='input-icon' />

            <input
              type={showPwd ? 'text' : 'password'}
              placeholder='Password'
              className='form-control auth-input'
              name='password'
              value={password}
              autoComplete='off'
              onChange={handleChange}
            />

            <button type='button' onClick={() => setShowPwd(!showPwd)} className='password-toggle-btn'>
              {showPwd ? <BsFillEyeFill /> : <BsFillEyeSlashFill />}
            </button>
          </div>

          {errors.password && <p className='text-danger insta-smart-error'>{errors.password}</p>}
        </div>

        <div className='d-flex align-items-center justify-content-center my-3'>
          <ReCAPTCHA ref={recaptchaRef} sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY} badge='inline' data-size='invisible' onChange={(value) => setCaptchaValue(value)} />
        </div>

        <div className='pt-2'>
          <button className='btn-submit-login outfit-bold max-width-100 d-flex align-items-center justify-content-center gap-2' onClick={handleClick}>
            {loader && <ButtonLoader />}
            <span className='outfit-bold'>Sign in</span>
          </button>
        </div>

        <div className='text-center mt-3'>
          <p className='terms-text outfit-regular'>
            By continuing, you agree to our{' '}
            <a href='https://www.comniqai.com/terms-of-service' target='_blank' rel='noopener noreferrer' className='terms-link outfit-medium'>
              Terms of Service
            </a>{' '}
            and{' '}
            <a href='https://www.comniqai.com/privacy-policy' target='_blank' rel='noopener noreferrer' className='terms-link outfit-medium'>
              Privacy Policy
            </a>
            .
          </p>
        </div>
      </form>
    </AuthLayout>
  );
};

export default LoginForm;
